<script setup lang="ts">
import { computed, ref } from 'vue';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import {
  medicationsApi, type AsNeededMedication, type DueDose, type MedicationAdministration,
} from '@/api/medications';
import { usePermissions } from '@/composables/usePermissions';
import { apiErrorText } from '@/utils/api-errors';
import { doseStatusColor, doseStatusText, recordPeople } from '@/utils/medication-doses';
import ResponsiveDialog from '@/components/responsive-dialog.vue';
import MedicationAdministrationDialog from './medication-administration-dialog.vue';

// The administration record of one day: each due dose with what happened, the
// as-needed medications and the extra doses. The office records what
// caregivers reported and voids mistakes, unless the module is off (`readOnly`).
const props = defineProps<{ patientId: number; readOnly?: boolean }>();

const { hasPermission } = usePermissions();
const queryClient = useQueryClient();
const canAdminister = computed(() => !props.readOnly && hasPermission.value('medications', 'administer'));

function isoDate(date: Date): string {
  return date.toLocaleDateString('sv-SE');
}

const date = ref(isoDate(new Date()));
const recording = ref<{ dose: DueDose | AsNeededMedication; mode: 'scheduled' | 'asNeeded' | 'extra' } | null>(null);
const voiding = ref<MedicationAdministration | null>(null);
const voidReason = ref('');
const error = ref('');

const { data, isPending } = useQuery({
  queryKey: ['medicationDoses', computed(() => props.patientId), date],
  queryFn: () => medicationsApi.doses(props.patientId, date.value),
});

function shiftDay(days: number) {
  const next = new Date(`${date.value}T12:00:00`);
  next.setDate(next.getDate() + days);
  date.value = isoDate(next);
}

function asNeededSubtitle(medication: AsNeededMedication): string {
  return [medication.summary, medication.indication, `${medication.givenLast24h} en 24 h`]
    .filter(Boolean).join(' · ');
}

const voidRecord = useMutation({
  mutationFn: () => medicationsApi.voidAdministration(voiding.value!.id, voidReason.value),
  onSuccess: () => {
    voiding.value = null;
    voidReason.value = '';
    queryClient.invalidateQueries({ queryKey: ['medicationDoses', props.patientId] });
  },
  onError: (failure) => {
    error.value = apiErrorText(failure, 'No se pudo anular el registro.');
  },
});
</script>
<template>
  <div class="d-flex flex-column ga-4">
    <div class="d-flex align-center ga-2">
      <v-btn
        icon="mdi-chevron-left"
        variant="text"
        aria-label="Día anterior"
        @click="shiftDay(-1)"
      />
      <v-text-field
        v-model="date"
        type="date"
        label="Día"
        variant="outlined"
        density="compact"
        hide-details
        class="date-field"
      />
      <v-btn
        icon="mdi-chevron-right"
        variant="text"
        aria-label="Día siguiente"
        @click="shiftDay(1)"
      />
    </div>
    <v-alert
      v-if="error"
      type="error"
      variant="tonal"
      :text="error"
      closable
      @click:close="error = ''"
    />
    <v-progress-linear
      v-if="isPending"
      indeterminate
    />

    <template v-if="data">
      <p
        v-if="data.doses.length === 0"
        class="text-body-2 text-medium-emphasis"
      >
        Sin dosis con horario este día.
      </p>
      <v-list
        v-else
        lines="two"
        density="compact"
      >
        <v-list-item
          v-for="dose in data.doses"
          :key="`${dose.dosageId}-${dose.scheduledAt}`"
        >
          <template #prepend>
            <span class="time font-weight-bold mr-4">{{ dose.scheduledTime }}</span>
          </template>
          <v-list-item-title>
            {{ dose.drugName }}
            <v-chip
              v-if="dose.highAlert"
              color="error"
              size="x-small"
              variant="tonal"
              class="ml-1"
            >
              Alto riesgo
            </v-chip>
          </v-list-item-title>
          <v-list-item-subtitle>
            {{ dose.summary }}
            <template v-if="dose.administration">
              · {{ recordPeople(dose.administration) }}
              <span v-if="dose.administration.reason">· {{ dose.administration.reason }}</span>
              <span v-if="dose.administration.offSchedule">· fuera de horario</span>
              <span
                v-if="dose.administration.consumption === 'failed'"
                class="text-warning"
              >· sin descuento de stock</span>
            </template>
          </v-list-item-subtitle>
          <template #append>
            <div class="d-flex align-center ga-2">
              <v-chip
                :color="doseStatusColor(dose)"
                size="small"
                variant="tonal"
              >
                {{ doseStatusText(dose) }}
              </v-chip>
              <v-menu
                v-if="canAdminister"
                location="bottom end"
              >
                <template #activator="{ props: menuProps }">
                  <v-btn
                    v-bind="menuProps"
                    icon="mdi-dots-vertical"
                    size="small"
                    variant="text"
                    aria-label="Más acciones"
                  />
                </template>
                <v-list density="compact">
                  <v-list-item
                    v-if="!dose.administration"
                    title="Registrar"
                    @click="recording = { dose, mode: 'scheduled' }"
                  />
                  <v-list-item
                    v-if="dose.administration"
                    title="Anular registro"
                    @click="voiding = dose.administration"
                  />
                  <v-list-item
                    title="Dosis extra"
                    @click="recording = { dose, mode: 'extra' }"
                  />
                </v-list>
              </v-menu>
            </div>
          </template>
        </v-list-item>
      </v-list>

      <template v-if="data.asNeeded.length > 0">
        <div class="text-subtitle-2 font-weight-bold">
          SOS
        </div>
        <v-list density="compact">
          <v-list-item
            v-for="medication in data.asNeeded"
            :key="medication.dosageId"
            :title="medication.drugName"
            :subtitle="asNeededSubtitle(medication)"
          >
            <template
              v-if="canAdminister"
              #append
            >
              <v-btn
                size="small"
                variant="tonal"
                @click="recording = { dose: medication, mode: 'asNeeded' }"
              >
                Registrar
              </v-btn>
            </template>
          </v-list-item>
        </v-list>
      </template>

      <template v-if="data.unscheduled.length > 0">
        <div class="text-subtitle-2 font-weight-bold">
          Dosis SOS y extra del día
        </div>
        <v-list density="compact">
          <v-list-item
            v-for="record in data.unscheduled"
            :key="record.id"
            :title="`${record.givenTime} · ${record.extraDose ? 'Dosis extra' : 'SOS'}`"
            :subtitle="[recordPeople(record), record.reason].filter(Boolean).join(' · ')"
          >
            <template
              v-if="canAdminister"
              #append
            >
              <v-btn
                size="small"
                variant="text"
                @click="voiding = record"
              >
                Anular
              </v-btn>
            </template>
          </v-list-item>
        </v-list>
      </template>
    </template>

    <medication-administration-dialog
      v-if="recording"
      :patient-id="patientId"
      :dose="recording.dose"
      :mode="recording.mode"
      @close="recording = null"
    />
    <responsive-dialog
      :model-value="!!voiding"
      title="Anular registro"
      max-width="480"
      @close="voiding = null"
    >
      <div class="d-flex flex-column ga-3 pt-2">
        <p class="text-body-2">
          El registro queda en el historial como anulado y, si se descontó del inventario, vuelve al stock.
        </p>
        <v-text-field
          v-model="voidReason"
          label="Motivo"
          variant="outlined"
          density="compact"
          hide-details
        />
      </div>
      <template #actions>
        <v-spacer />
        <v-btn @click="voiding = null">
          Cancelar
        </v-btn>
        <v-btn
          color="error"
          variant="flat"
          :disabled="!voidReason.trim()"
          :loading="voidRecord.isPending.value"
          @click="voidRecord.mutate()"
        >
          Anular
        </v-btn>
      </template>
    </responsive-dialog>
  </div>
</template>
<style scoped>
.date-field {
  max-width: 200px;
}

.time {
  min-width: 48px;
}
</style>
